import { Box } from '@mui/material';
import type { Editor } from '@tiptap/react';
import React, { useEffect, useRef, useState } from 'react';
import { SimpleEditor } from 'shared-components/components/TipTap/tiptap-templates/simple/simple-editor';
import { TextEditorActions } from './TextEditorActions';
import { TextEditorSkeleton } from './TextEditorSkeleton';
import { useEditableDocumentMutations } from './useEditableDocumentMutations';
import type { EditableDocumentMutations, EditableDocumentQueryResult } from './useEditableDocumentMutations';

export type EditableDocumentAction = 'save' | 'confirm' | 'discard' | 'revert';

export interface TextEditorVersionActionsRef {
  current: {
    revertToVersion: (version: number) => void;
    discardThenRevertToVersion: (version: number) => void;
  } | null;
}

export interface TextEditorActionsBarRenderProps {
  data: ReturnType<typeof useEditableDocumentMutations>['data'];
  mutations: EditableDocumentMutations;
  isEditorReady: boolean;
  approveDisabled: boolean;
  onDiscardRequested: () => void;
  versionActionsRef?: TextEditorVersionActionsRef;
}

export interface TextEditorProps {
  editableDocumentId?: string;
  documentQuery?: EditableDocumentQueryResult;
  renderActionsBar?: (props: TextEditorActionsBarRenderProps) => React.ReactNode;
  versionActionsRef?: TextEditorVersionActionsRef;
  onEditorReady?: (editor: Editor | null) => void;
  onContentChange?: (html: string) => void;
  onContentUpdate?: (html: string, version: number) => void;
  /** Actions to display for editable documents
   * Use 'all' to show all actions, or provide an array of specific actions */
  visibleActions?: 'all' | EditableDocumentAction[];
  toolbarContent?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: (error: Error) => React.ReactNode;
  autosaveEnabled?: boolean;
  /** Interval in milliseconds for autosave (default: 30000 = 30 seconds) */
  autosaveInterval?: number;
  /** Whether the editor is editable. Defaults to true. */
  editable?: boolean;
  onConfirm?: () => void;
  onDiscard?: () => void;
  onSave?: (version: number | null) => void;
  /** Seed the editor with this HTML on first mount. Takes precedence over the document query result. */
  initialContent?: string | Record<string, unknown>;
}

export default function TextEditor({
  editableDocumentId,
  documentQuery,
  renderActionsBar,
  versionActionsRef,
  onEditorReady,
  onContentChange,
  onContentUpdate,
  visibleActions,
  toolbarContent,
  loadingComponent,
  errorComponent,
  autosaveEnabled = false,
  autosaveInterval = 30000, // 30 seconds default
  editable = true,
  onConfirm,
  onDiscard,
  onSave,
  initialContent,
}: TextEditorProps) {
  const editorRef = useRef<Editor | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isApproveDisabled, setIsApproveDisabled] = useState(false);
  /** Stored so the update-listener effect re-runs when the editor instance changes (not just isEditorReady). */
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const discardRequestedRef = useRef(false);

  // Tracks last loaded version. This is needed to prevent double setContent
  // calls when mutations update content then refetch returns same version
  const lastLoadedVersionRef = useRef<number | null>(null);

  // Only run autosave/unmount save after the user has actually edited (avoids save from HTML normalization)
  const userHasEditedRef = useRef(false);
  const lastContentWeSetRef = useRef<string | null>(null);

  // Refs for unmount save so we only run cleanup on unmount / document switch,
  // not on every re-render (avoid double onSave when deps like save.handle or data change)
  const saveOnUnmountRef = useRef<{
    editableDocumentId: string | undefined;
    document: { html?: string | null; confirmedAt?: string | null } | null;
    saveHandle: () => Promise<number | null>;
    saveLoading: boolean;
    confirmPipelineInProgressRef: React.MutableRefObject<boolean>;
    onSave: ((version: number | null) => void) | undefined;
    userHasEdited: boolean;
  } | null>(null);

  const editableDocumentMutations = useEditableDocumentMutations({
    documentId: editableDocumentId,
    editorRef,
    lastLoadedVersionRef,
    onContentUpdate,
    onConfirm,
    onDiscard,
    documentQuery,
    onContentSetFromServer: () => {
      const editor = editorRef.current;
      if (editor) {
        lastContentWeSetRef.current = editor.getHTML();
        userHasEditedRef.current = false;
      }
    },
  });

  // Reset editor ready state, version tracking, and discard flag when document changes
  useEffect(() => {
    setIsEditorReady(false);
    setEditorInstance(null);
    setIsApproveDisabled(false);
    lastLoadedVersionRef.current = null;
    discardRequestedRef.current = false;
    userHasEditedRef.current = false;
    lastContentWeSetRef.current = null;
  }, [editableDocumentId]);

  // Update editor content when document data changes
  // Skips if version is unchanged to avoid double setContent after mutations.
  // If user has unsaved edits and server html differs, don't overwrite (keeps changes made while new version was being created).
  useEffect(() => {
    const editor = editorRef.current;
    const html = editableDocumentMutations.data?.editableDocument?.html;
    const version = editableDocumentMutations.data?.editableDocument?.version;

    const { from, to } = editor?.state.selection ?? { from: 0, to: 0 };

    if (html && editor && isEditorReady && version !== null && version !== lastLoadedVersionRef.current) {
      const currentHtml = editor.getHTML();
      if (userHasEditedRef.current && currentHtml !== html) {
        return;
      }
      lastLoadedVersionRef.current = version;
      if (currentHtml !== html) {
        editor.commands.setContent(html);
        editor.commands.setTextSelection({ from, to });
      }
      lastContentWeSetRef.current = editor.getHTML();
      userHasEditedRef.current = false;
    }
  }, [
    editableDocumentMutations.data?.editableDocument?.html,
    editableDocumentMutations.data?.editableDocument?.version,
    isEditorReady,
  ]);

  // Handle editor ready callback
  const handleEditorReady = (editor: Editor | null) => {
    editorRef.current = editor;
    setIsEditorReady(!!editor);
    setEditorInstance(editor);
    onEditorReady?.(editor);
  };

  // Set up content change listener so userHasEditedRef is set when the user edits.
  // Depends on editorInstance (not just isEditorReady) so we re-attach when the editor is replaced.
  // Listen to both 'update' and 'transaction' so we catch all edits (transaction fires when doc changes).
  useEffect(() => {
    if (!editorInstance || !onContentChange) {
      return;
    }

    const markUserEdited = () => {
      const html = editorInstance.getHTML();
      if (html !== lastContentWeSetRef.current) {
        userHasEditedRef.current = true;
      }
    };

    const handleUpdate = () => {
      onContentChange(editorInstance.getHTML());
    };

    const handleTransaction = ({ transaction }: { transaction: { docChanged?: boolean } }) => {
      if (transaction.docChanged) {
        markUserEdited();
      }
    };

    editorInstance.on('update', handleUpdate);
    editorInstance.on('transaction', handleTransaction);
    return () => {
      editorInstance.off('update', handleUpdate);
      editorInstance.off('transaction', handleTransaction);
    };
  }, [editorInstance, onContentChange]);

  // Keep ref updated so cleanup always has latest values
  saveOnUnmountRef.current = {
    editableDocumentId,
    document: editableDocumentMutations.data?.editableDocument ?? null,
    saveHandle: editableDocumentMutations.mutations.save.handle,
    saveLoading: editableDocumentMutations.mutations.save.loading,
    confirmPipelineInProgressRef: editableDocumentMutations.confirmPipelineInProgressRef,
    onSave,
    userHasEdited: userHasEditedRef.current,
  };

  // Save on unmount when there are unsaved changes (skip if user chose discard).
  // Skip save if the document is already confirmed (e.g. user just clicked Approve):
  // saving would create a new version when active is confirmed.
  // Only depend on editableDocumentId so cleanup runs on real unmount or document switch,
  // not on every re-render (unstable save.handle / data caused double onSave).
  useEffect(() => {
    return () => {
      if (discardRequestedRef.current) {
        return;
      }
      const editor = editorRef.current;
      const {
        editableDocumentId: refDocId,
        document: doc,
        saveHandle,
        saveLoading,
        confirmPipelineInProgressRef,
        onSave: onSaveCb,
        userHasEdited,
      } = saveOnUnmountRef.current ?? {};
      if (refDocId !== editableDocumentId) {
        return;
      }
      if (!editableDocumentId || !editor || !doc || saveLoading) {
        return;
      }
      if (confirmPipelineInProgressRef?.current) {
        return;
      }
      if (doc.confirmedAt) {
        return;
      }
      const currentHtml = editor.getHTML();
      const savedHtml = doc.html;
      if (userHasEdited && currentHtml !== (savedHtml ?? '') && saveHandle) {
        saveHandle().then((version) => {
          if (version !== null) {
            onSaveCb?.(version);
          }
        });
      }
    };
  }, [editableDocumentId]);

  // Autosave functionality
  useEffect(() => {
    if (!autosaveEnabled || !editableDocumentId || !isEditorReady) {
      return;
    }

    const intervalId = setInterval(async () => {
      const editor = editorRef.current;
      const document = editableDocumentMutations.data?.editableDocument;
      if (!editor || !document || editableDocumentMutations.mutations.save.loading) {
        return;
      }
      // Don't autosave while approve pipeline runs (ref is synchronous; state lags a frame).
      if (editableDocumentMutations.confirmPipelineInProgressRef.current) {
        return;
      }
      // Don't autosave while confirm is in progress or after document is confirmed;
      // saving would create a new version when active is confirmed.
      if (editableDocumentMutations.mutations.confirm.loading) {
        return;
      }

      const currentHtml = editor.getHTML();
      const savedHtml = document.html;

      // Only save if user has edited and content has changed since last save
      if (userHasEditedRef.current && currentHtml !== (savedHtml ?? '')) {
        const version = await editableDocumentMutations.mutations.save.handle();
        onSave?.(version);
      }
    }, autosaveInterval);

    return () => clearInterval(intervalId);
  }, [
    autosaveEnabled,
    editableDocumentId,
    isEditorReady,
    autosaveInterval,
    editableDocumentMutations.mutations.save.handle,
    editableDocumentMutations.mutations.save.loading,
    editableDocumentMutations.confirmPipelineInProgressRef,
    editableDocumentMutations.mutations.confirm.loading,
    editableDocumentMutations.data?.editableDocument,
  ]);

  // Show loading state only on initial load (no data yet, editor not ready). Don't show skeleton on first edit/refetch.
  if (
    editableDocumentId &&
    editableDocumentMutations.loading &&
    !editableDocumentMutations.data?.editableDocument &&
    !isEditorReady
  ) {
    return loadingComponent || <TextEditorSkeleton />;
  }

  // Show error state
  if (editableDocumentId && editableDocumentMutations.error) {
    if (errorComponent) {
      return <>{errorComponent(editableDocumentMutations.error)}</>;
    }
    return <div>Error loading document: {editableDocumentMutations.error.message}</div>;
  }

  const documentTitle = editableDocumentMutations.data?.editableDocument?.title;
  const htmlContent = editableDocumentMutations.data?.editableDocument?.html ?? undefined;
  const showDefaultActionsBar =
    editableDocumentId &&
    !renderActionsBar &&
    visibleActions &&
    (visibleActions === 'all' || visibleActions.length > 0);
  const actionsBarProps = {
    data: editableDocumentMutations.data,
    mutations: editableDocumentMutations.mutations,
    isEditorReady,
    approveDisabled: isApproveDisabled,
    onDiscardRequested: (): void => {
      discardRequestedRef.current = true;
    },
    versionActionsRef,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        borderBottom: '1px solid #E0E0E0',
      }}>
      {editableDocumentId && renderActionsBar?.(actionsBarProps)}
      {showDefaultActionsBar && (
        <TextEditorActions
          data={editableDocumentMutations.data}
          mutations={editableDocumentMutations.mutations}
          isEditorReady={isEditorReady}
          approveDisabled={isApproveDisabled}
          visibleActions={visibleActions!}
          title={documentTitle}
          onDiscardRequested={actionsBarProps.onDiscardRequested}
        />
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <SimpleEditor
          key={editableDocumentId}
          mode="full"
          initialContent={initialContent ?? htmlContent}
          onEditorReady={handleEditorReady}
          toolbarContent={toolbarContent}
          editable={editable}
          imageUpload={editableDocumentMutations.mutations.attachImage.handle}
          onImageUploadingChange={setIsApproveDisabled}
        />
      </Box>
    </Box>
  );
}
